import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import USPhoneInput from "../components/USPhoneInput";
import Footer from "../components/Footer";

const inputClass =
  "w-full font-constantia text-black-deep bg-white-pure border border-gray-200 rounded-lg px-4 py-2.5 text-sm leading-relaxed outline-none transition-all duration-200 placeholder-gray-400 focus:border-blue-primary focus:shadow-[0_0_0_3px_rgba(0,45,181,0.08)]";
const labelClass = "block font-constantia text-black-deep mb-1.5";
const labelStyle = { fontSize: "14px", opacity: 0.8 };
const req = <span className="text-red-500 ml-0.5">*</span>;

const REQUEST_TYPES = [
  {
    value: "coi",
    label: "Certificate of Insurance",
    short: "COI",
    description: "Request a certificate for a lender, landlord, or third party.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
        <line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/>
      </svg>
    ),
  },
  {
    value: "policy-change",
    label: "Policy Change",
    short: "Change",
    description: "Add or remove a vehicle, driver, or update your coverage.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
      </svg>
    ),
  },
  {
    value: "contact-update",
    label: "Update Contact Info",
    short: "Update",
    description: "Change your address, phone number, or email on file.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  },
  {
    value: "claim",
    label: "Report a Claim",
    short: "Claim",
    description: "Report an accident, loss, or damage and start the claims process.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
  },
  {
    value: "call-request",
    label: "Request a Call",
    short: "Call",
    description: "Have a licensed agent call you at a convenient time.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
      </svg>
    ),
  },
];

const CHANGE_TYPES = [
  "Add a Vehicle", "Remove a Vehicle",
  "Add a Driver",  "Remove a Driver",
  "Update Coverage Limits", "Change Address on Policy", "Other",
];

const LOSS_TYPES = ["Auto Accident", "Property Damage", "Theft", "Liability", "Water Damage", "Other"];

const CONTACT_FIELDS = ["Name", "Mailing Address", "Phone Number", "Email Address", "Other"];

const CALL_TIMES = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
];

// ─── Step indicator ────────────────────────────────────────────────────────────
function StepBar({ step }) {
  const labels = ["Request Type", "Your Info", "Details", "Review"];
  return (
    <div className="flex items-center justify-center mb-8">
      {labels.map((label, i) => {
        const n      = i + 1;
        const done   = step > n;
        const active = step === n;
        return (
          <div key={label} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center font-cinzel font-bold transition-all duration-300"
                style={{
                  fontSize: "12px",
                  backgroundColor: done ? "#012E72" : active ? "#002DB5" : "rgba(1,46,114,0.1)",
                  color: done || active ? "#FFFFFF" : "rgba(1,46,114,0.4)",
                }}
              >
                {done ? (
                  <svg width="12" height="12" viewBox="0 0 10 10" fill="none">
                    <polyline points="1.5 5 4 7.5 8.5 2.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : n}
              </div>
              <span
                className="font-cinzel uppercase hidden sm:block"
                style={{ fontSize: "9px", letterSpacing: "0.12em", color: active ? "#012E72" : "rgba(1,46,114,0.35)", whiteSpace: "nowrap" }}
              >
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div
                className="w-10 sm:w-14 h-px mx-1.5 mb-5 transition-colors duration-300"
                style={{ backgroundColor: step > n ? "#012E72" : "rgba(1,46,114,0.15)" }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Review row ───────────────────────────────────────────────────────────────
function ReviewRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-4 px-5 py-3 border-b last:border-0" style={{ borderColor: "rgba(1,46,114,0.07)" }}>
      <span className="font-cinzel uppercase flex-shrink-0 w-32 pt-0.5" style={{ fontSize: "9px", letterSpacing: "0.15em", color: "rgba(1,46,114,0.5)" }}>
        {label}
      </span>
      <span className="font-constantia text-black-deep" style={{ fontSize: "14px", opacity: 0.85 }}>{value}</span>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ServiceRequestPage() {
  usePageTitle("Service Request");
  const navigate = useNavigate();

  const [step, setStep]             = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);

  const [form, setForm] = useState({
    requestType: "",
    fullName: "", email: "", phone: "", policyNumber: "",
    notes: "",
    // COI
    certificateHolder: "", certificateHolderAddress: "", additionalInsured: "", deliveryMethod: "email",
    // Policy change
    changeType: "", changeDescription: "", effectiveDate: "",
    // Contact update
    fieldsToUpdate: [], newInformation: "",
    // Claim
    dateOfLoss: "", lossType: "", lossLocation: "", claimDescription: "", injuries: "", otherParties: "",
    // Call request
    preferredCallTime: "", callReason: "",
  });

  const set = (name, value) => setForm((p) => ({ ...p, [name]: value }));
  const handleChange = (e) => set(e.target.name, e.target.value);

  const toggleField = (field) => {
    setForm((p) => {
      const arr = p.fieldsToUpdate.includes(field)
        ? p.fieldsToUpdate.filter((f) => f !== field)
        : [...p.fieldsToUpdate, field];
      return { ...p, fieldsToUpdate: arr };
    });
  };

  const goToStep = (n) => { setError(null); setStep(n); window.scrollTo(0, 0); };

  const typeInfo = REQUEST_TYPES.find((t) => t.value === form.requestType);

  const step1Valid = !!form.requestType;
  const step2Valid = form.fullName && form.email && form.phone;
  const step3Valid = (() => {
    switch (form.requestType) {
      case "coi":          return !!form.certificateHolder;
      case "policy-change": return !!form.changeType && !!form.changeDescription;
      case "contact-update": return form.fieldsToUpdate.length > 0 && !!form.newInformation;
      case "claim":        return !!form.dateOfLoss && !!form.lossType && !!form.claimDescription;
      case "call-request": return !!form.preferredCallTime && !!form.callReason;
      default:             return false;
    }
  })();

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = { ...form, fieldsToUpdate: form.fieldsToUpdate.join(", ") };
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/service-requests`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Submission failed.");
      setStep(5);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const cardTitles = {
    1: "What do you need help with?",
    2: "Your Contact Information",
    3: typeInfo ? typeInfo.label : "Request Details",
    4: "Review Your Request",
  };
  const cardSubtitles = {
    1: "Select the type of service request you'd like to submit.",
    2: "We'll use this to process your request and follow up with you.",
    3: "Provide the details we need to complete your request.",
    4: "Confirm everything looks right before we submit.",
  };

  return (
    <>
      <main className="min-h-screen w-full">

        {/* ── Hero ── */}
        <section className="relative bg-blue-dark overflow-hidden py-20 sm:py-28">
          <div className="absolute inset-0 pointer-events-none" style={{
            backgroundImage: `radial-gradient(ellipse 80% 70% at 50% 0%, rgba(0,45,181,0.4) 0%, transparent 70%),
                              radial-gradient(ellipse 50% 45% at 100% 100%, rgba(0,20,90,0.35) 0%, transparent 60%)`,
          }} />
          <div className="absolute inset-0 pointer-events-none" style={{
            backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)",
            backgroundSize: "80px 80px",
          }} />
          <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            backgroundSize: "200px 200px",
          }} />

          <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <div className="inline-flex items-center gap-2 mb-7">
              <div className="w-6 h-px bg-white-pure/30" />
              <span className="font-cinzel font-bold text-[0.58rem] tracking-[0.3em] uppercase text-white-pure/55 border border-white-pure/18 px-4 py-[5px] rounded-full">
                Existing Policyholders
              </span>
              <div className="w-6 h-px bg-white-pure/30" />
            </div>

            <h1
              className="font-cinzel font-black text-white-pure tracking-[0.04em] leading-[1.1] mb-5"
              style={{ fontSize: "clamp(1.55rem, 4.5vw, 3rem)", textShadow: "0 2px 48px rgba(0,45,181,0.55)" }}
            >
              SUBMIT A<br className="hidden sm:block" /> SERVICE REQUEST
            </h1>

            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="w-12 h-px bg-gradient-to-r from-transparent to-white-pure/30" />
              <div className="w-1.5 h-1.5 rounded-full bg-white-pure/40 border border-white-pure/55" />
              <div className="w-12 h-px bg-gradient-to-l from-transparent to-white-pure/30" />
            </div>

            <p className="font-constantia italic text-white-pure/55 leading-[1.85] max-w-xl mx-auto" style={{ fontSize: "clamp(0.88rem, 1.6vw, 1.05rem)" }}>
              Need a certificate, policy update, or want to report a claim? Submit your request online and a licensed agent will follow up promptly.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
              {["Fast processing", "Licensed agents", "Secure submission", "Mon – Fri 9 AM – 5 PM"].map((b) => (
                <span key={b} className="font-cinzel text-[0.6rem] tracking-[0.15em] uppercase text-white-pure/50 border border-white-pure/15 px-4 py-[5px] rounded-full">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── Form section ── */}
        <section className="py-16 sm:py-20 lg:py-24" style={{ background: "linear-gradient(160deg,#EEF2F8 0%,#E8EDF6 50%,#EDF1F9 100%)" }}>
          <div className="max-w-2xl mx-auto px-4 sm:px-6">

            {/* ── Success ── */}
            {step === 5 ? (
              <div className="bg-white-pure rounded-2xl border border-gray-200 p-10 text-center shadow-sm">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: "rgba(1,46,114,0.08)" }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2 className="font-cinzel font-bold text-blue-dark uppercase mb-3" style={{ fontSize: "20px", letterSpacing: "0.08em" }}>
                  Request Submitted!
                </h2>
                <p className="font-constantia text-black-deep leading-relaxed mb-2 max-w-md mx-auto" style={{ fontSize: "15px", opacity: 0.65 }}>
                  Thank you, <strong>{form.fullName}</strong>. We've received your{" "}
                  <strong>{typeInfo?.label}</strong> request and will be in touch shortly.
                </p>
                <p className="font-constantia italic mb-8" style={{ fontSize: "13px", color: "rgba(1,7,4,0.4)" }}>
                  Confirmation sent to <strong>{form.email}</strong>
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    onClick={() => navigate("/")}
                    className="font-cinzel uppercase tracking-widest bg-blue-dark text-white-pure px-8 py-3 rounded-full hover:bg-blue-primary transition-colors duration-200"
                    style={{ fontSize: "11px" }}
                  >
                    Back to Home
                  </button>
                  <button
                    onClick={() => navigate("/consultation")}
                    className="font-cinzel uppercase tracking-widest text-blue-dark border border-blue-dark/30 px-8 py-3 rounded-full hover:bg-blue-dark hover:text-white-pure transition-colors duration-200"
                    style={{ fontSize: "11px" }}
                  >
                    Book a Consultation
                  </button>
                </div>
              </div>
            ) : (
              <>
                <StepBar step={step} />

                <div className="bg-white-pure rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

                  {/* Card header */}
                  <div className="px-8 py-5 border-b border-gray-100">
                    <h2 className="font-cinzel font-bold text-blue-dark" style={{ fontSize: "16px", letterSpacing: "0.05em" }}>
                      {cardTitles[step]}
                    </h2>
                    <p className="font-constantia mt-1" style={{ fontSize: "13px", color: "rgba(1,7,4,0.45)" }}>
                      {cardSubtitles[step]}
                    </p>
                  </div>

                  <div className="px-8 py-6">

                    {/* ── Step 1: Request type ── */}
                    {step === 1 && (
                      <div className="flex flex-col gap-3">
                        {REQUEST_TYPES.map((type) => {
                          const active = form.requestType === type.value;
                          return (
                            <button
                              key={type.value}
                              type="button"
                              onClick={() => set("requestType", type.value)}
                              className="w-full text-left flex items-start gap-4 px-5 py-4 rounded-xl transition-all duration-200"
                              style={{
                                backgroundColor: active ? "rgba(1,46,114,0.06)" : "rgba(1,46,114,0.02)",
                                border: active ? "2px solid rgba(1,46,114,0.5)" : "2px solid rgba(1,46,114,0.1)",
                                boxShadow: active ? "0 4px 14px rgba(1,46,114,0.1)" : "none",
                              }}
                            >
                              <div
                                className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mt-0.5 transition-colors duration-200"
                                style={{
                                  backgroundColor: active ? "rgba(1,46,114,0.12)" : "rgba(1,46,114,0.06)",
                                  color: active ? "#012E72" : "rgba(1,46,114,0.5)",
                                }}
                              >
                                {type.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-cinzel font-bold text-blue-dark" style={{ fontSize: "13px", letterSpacing: "0.05em" }}>
                                  {type.label}
                                </p>
                                <p className="font-constantia mt-0.5" style={{ fontSize: "13px", color: "rgba(1,7,4,0.5)" }}>
                                  {type.description}
                                </p>
                              </div>
                              <div
                                className="flex-shrink-0 w-5 h-5 rounded-full border-2 mt-2.5 transition-all duration-200 flex items-center justify-center"
                                style={{
                                  borderColor: active ? "#012E72" : "rgba(1,46,114,0.2)",
                                  backgroundColor: active ? "#012E72" : "transparent",
                                }}
                              >
                                {active && (
                                  <div className="w-2 h-2 rounded-full bg-white-pure" />
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* ── Step 2: Your info ── */}
                    {step === 2 && (
                      <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className={labelClass} style={labelStyle}>Full name {req}</label>
                            <input type="text" name="fullName" placeholder="John Doe" value={form.fullName} onChange={handleChange} className={inputClass} />
                          </div>
                          <div>
                            <label className={labelClass} style={labelStyle}>Email address {req}</label>
                            <input type="email" name="email" placeholder="john@example.com" value={form.email} onChange={handleChange} className={inputClass} />
                          </div>
                          <USPhoneInput name="phone" value={form.phone} onChange={handleChange} label="Phone number" required />
                          <div>
                            <label className={labelClass} style={labelStyle}>
                              Policy number{" "}
                              <span className="font-constantia" style={{ fontSize: "12px", opacity: 0.5 }}>(optional)</span>
                            </label>
                            <input type="text" name="policyNumber" placeholder="e.g. PAL-12345" value={form.policyNumber} onChange={handleChange} className={inputClass} />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Step 3: Type-specific details ── */}
                    {step === 3 && (
                      <div className="flex flex-col gap-4">

                        {/* COI */}
                        {form.requestType === "coi" && (
                          <>
                            <div>
                              <label className={labelClass} style={labelStyle}>Certificate holder name {req}</label>
                              <input type="text" name="certificateHolder" placeholder="Company or individual requiring the certificate" value={form.certificateHolder} onChange={handleChange} className={inputClass} />
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Certificate holder address</label>
                              <input type="text" name="certificateHolderAddress" placeholder="Street, city, state, ZIP" value={form.certificateHolderAddress} onChange={handleChange} className={inputClass} />
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Additional insured</label>
                              <input type="text" name="additionalInsured" placeholder="Name to list as additional insured, if any" value={form.additionalInsured} onChange={handleChange} className={inputClass} />
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Delivery method {req}</label>
                              <div className="flex gap-3">
                                {["email", "fax", "mail"].map((m) => {
                                  const active = form.deliveryMethod === m;
                                  return (
                                    <button
                                      key={m}
                                      type="button"
                                      onClick={() => set("deliveryMethod", m)}
                                      className="flex-1 py-2.5 rounded-lg font-cinzel uppercase transition-all duration-200"
                                      style={{
                                        fontSize: "11px",
                                        letterSpacing: "0.1em",
                                        backgroundColor: active ? "#012E72" : "rgba(1,46,114,0.04)",
                                        color: active ? "#FFFFFF" : "#012E72",
                                        border: active ? "1.5px solid #012E72" : "1.5px solid rgba(1,46,114,0.15)",
                                      }}
                                    >
                                      {m === "email" ? "Email" : m === "fax" ? "Fax" : "Mail"}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Additional notes</label>
                              <textarea name="notes" rows={3} placeholder="Any special instructions or requirements..." value={form.notes} onChange={handleChange} className={`${inputClass} resize-none`} />
                            </div>
                          </>
                        )}

                        {/* Policy change */}
                        {form.requestType === "policy-change" && (
                          <>
                            <div>
                              <label className={labelClass} style={labelStyle}>Type of change {req}</label>
                              <select name="changeType" value={form.changeType} onChange={handleChange} className={inputClass}>
                                <option value="">Select a change type...</option>
                                {CHANGE_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Describe the change {req}</label>
                              <textarea name="changeDescription" rows={4} placeholder="Provide specific details about what needs to change..." value={form.changeDescription} onChange={handleChange} className={`${inputClass} resize-none`} />
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Requested effective date</label>
                              <input type="date" name="effectiveDate" value={form.effectiveDate} onChange={handleChange} className={inputClass} style={{ colorScheme: "light" }} />
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Additional notes</label>
                              <textarea name="notes" rows={2} placeholder="Anything else we should know..." value={form.notes} onChange={handleChange} className={`${inputClass} resize-none`} />
                            </div>
                          </>
                        )}

                        {/* Contact update */}
                        {form.requestType === "contact-update" && (
                          <>
                            <div>
                              <label className={labelClass} style={labelStyle}>What needs to be updated? {req}</label>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {CONTACT_FIELDS.map((f) => {
                                  const checked = form.fieldsToUpdate.includes(f);
                                  return (
                                    <button
                                      key={f}
                                      type="button"
                                      onClick={() => toggleField(f)}
                                      className="px-4 py-2 rounded-lg font-cinzel uppercase transition-all duration-200"
                                      style={{
                                        fontSize: "10px",
                                        letterSpacing: "0.1em",
                                        backgroundColor: checked ? "#012E72" : "rgba(1,46,114,0.05)",
                                        color: checked ? "#FFFFFF" : "#012E72",
                                        border: checked ? "1.5px solid #012E72" : "1.5px solid rgba(1,46,114,0.15)",
                                      }}
                                    >
                                      {f}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>New information {req}</label>
                              <textarea name="newInformation" rows={4} placeholder="Provide the updated details (e.g. new address: 123 Main St, Tampa, FL 33601)..." value={form.newInformation} onChange={handleChange} className={`${inputClass} resize-none`} />
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Additional notes</label>
                              <textarea name="notes" rows={2} placeholder="Anything else we should know..." value={form.notes} onChange={handleChange} className={`${inputClass} resize-none`} />
                            </div>
                          </>
                        )}

                        {/* Claim */}
                        {form.requestType === "claim" && (
                          <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className={labelClass} style={labelStyle}>Date of loss {req}</label>
                                <input type="date" name="dateOfLoss" value={form.dateOfLoss} onChange={handleChange} className={inputClass} style={{ colorScheme: "light" }} />
                              </div>
                              <div>
                                <label className={labelClass} style={labelStyle}>Type of loss {req}</label>
                                <select name="lossType" value={form.lossType} onChange={handleChange} className={inputClass}>
                                  <option value="">Select...</option>
                                  {LOSS_TYPES.map((l) => <option key={l} value={l}>{l}</option>)}
                                </select>
                              </div>
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Location of loss</label>
                              <input type="text" name="lossLocation" placeholder="Address or description of where the loss occurred" value={form.lossLocation} onChange={handleChange} className={inputClass} />
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Describe what happened {req}</label>
                              <textarea name="claimDescription" rows={4} placeholder="Provide a clear description of the incident..." value={form.claimDescription} onChange={handleChange} className={`${inputClass} resize-none`} />
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Were there any injuries?</label>
                              <input type="text" name="injuries" placeholder="None, or describe any injuries to any party" value={form.injuries} onChange={handleChange} className={inputClass} />
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Other parties involved</label>
                              <textarea name="otherParties" rows={2} placeholder="Names, contact info, or insurance details of other parties..." value={form.otherParties} onChange={handleChange} className={`${inputClass} resize-none`} />
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Additional notes</label>
                              <textarea name="notes" rows={2} placeholder="Police report number, photos available, etc..." value={form.notes} onChange={handleChange} className={`${inputClass} resize-none`} />
                            </div>
                          </>
                        )}

                        {/* Call request */}
                        {form.requestType === "call-request" && (
                          <>
                            <div>
                              <label className={labelClass} style={labelStyle}>Preferred call time {req}</label>
                              <div className="grid grid-cols-5 gap-2">
                                {CALL_TIMES.map((t) => {
                                  const active = form.preferredCallTime === t;
                                  return (
                                    <button
                                      key={t}
                                      type="button"
                                      onClick={() => set("preferredCallTime", t)}
                                      className="py-2.5 rounded-lg font-cinzel uppercase transition-all duration-200 hover:-translate-y-0.5"
                                      style={{
                                        fontSize: "10px",
                                        letterSpacing: "0.06em",
                                        backgroundColor: active ? "#012E72" : "rgba(1,46,114,0.05)",
                                        color: active ? "#FFFFFF" : "#012E72",
                                        border: active ? "1.5px solid #012E72" : "1.5px solid rgba(1,46,114,0.15)",
                                        boxShadow: active ? "0 4px 14px rgba(1,46,114,0.25)" : "none",
                                      }}
                                    >
                                      {t}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <div>
                              <label className={labelClass} style={labelStyle}>Reason for call {req}</label>
                              <textarea name="callReason" rows={4} placeholder="Briefly describe what you'd like to discuss..." value={form.callReason} onChange={handleChange} className={`${inputClass} resize-none`} />
                            </div>
                          </>
                        )}

                      </div>
                    )}

                    {/* ── Step 4: Review ── */}
                    {step === 4 && (
                      <div className="flex flex-col gap-4">

                        <div>
                          <p className="font-cinzel uppercase tracking-widest mb-2" style={{ fontSize: "10px", color: "rgba(1,46,114,0.45)" }}>Request Type</p>
                          <div className="rounded-xl border border-gray-200 overflow-hidden">
                            <ReviewRow label="Type" value={typeInfo?.label} />
                          </div>
                        </div>

                        <div>
                          <p className="font-cinzel uppercase tracking-widest mb-2" style={{ fontSize: "10px", color: "rgba(1,46,114,0.45)" }}>Contact Info</p>
                          <div className="rounded-xl border border-gray-200 overflow-hidden">
                            <ReviewRow label="Name"          value={form.fullName} />
                            <ReviewRow label="Email"         value={form.email} />
                            <ReviewRow label="Phone"         value={form.phone} />
                            <ReviewRow label="Policy #"      value={form.policyNumber || "—"} />
                          </div>
                        </div>

                        <div>
                          <p className="font-cinzel uppercase tracking-widest mb-2" style={{ fontSize: "10px", color: "rgba(1,46,114,0.45)" }}>Request Details</p>
                          <div className="rounded-xl border border-gray-200 overflow-hidden">
                            {form.requestType === "coi" && (
                              <>
                                <ReviewRow label="Cert Holder"    value={form.certificateHolder} />
                                <ReviewRow label="Holder Address" value={form.certificateHolderAddress || "—"} />
                                <ReviewRow label="Add'l Insured"  value={form.additionalInsured || "—"} />
                                <ReviewRow label="Delivery"       value={form.deliveryMethod} />
                                <ReviewRow label="Notes"          value={form.notes || "—"} />
                              </>
                            )}
                            {form.requestType === "policy-change" && (
                              <>
                                <ReviewRow label="Change Type"    value={form.changeType} />
                                <ReviewRow label="Description"    value={form.changeDescription} />
                                <ReviewRow label="Effective Date" value={form.effectiveDate || "—"} />
                                <ReviewRow label="Notes"          value={form.notes || "—"} />
                              </>
                            )}
                            {form.requestType === "contact-update" && (
                              <>
                                <ReviewRow label="Update Fields"  value={form.fieldsToUpdate.join(", ")} />
                                <ReviewRow label="New Info"       value={form.newInformation} />
                                <ReviewRow label="Notes"          value={form.notes || "—"} />
                              </>
                            )}
                            {form.requestType === "claim" && (
                              <>
                                <ReviewRow label="Date of Loss"   value={form.dateOfLoss} />
                                <ReviewRow label="Loss Type"      value={form.lossType} />
                                <ReviewRow label="Location"       value={form.lossLocation || "—"} />
                                <ReviewRow label="Description"    value={form.claimDescription} />
                                <ReviewRow label="Injuries"       value={form.injuries || "—"} />
                                <ReviewRow label="Other Parties"  value={form.otherParties || "—"} />
                                <ReviewRow label="Notes"          value={form.notes || "—"} />
                              </>
                            )}
                            {form.requestType === "call-request" && (
                              <>
                                <ReviewRow label="Call Time"  value={form.preferredCallTime} />
                                <ReviewRow label="Reason"     value={form.callReason} />
                              </>
                            )}
                          </div>
                        </div>

                      </div>
                    )}

                    {error && (
                      <div className="mt-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200">
                        <p className="font-constantia text-red-700 text-sm">{error}</p>
                      </div>
                    )}

                    {/* Nav buttons */}
                    <div className="flex gap-3 mt-6">
                      {step > 1 && (
                        <button
                          onClick={() => goToStep(step - 1)}
                          className="flex-1 font-cinzel uppercase tracking-widest text-blue-dark border border-blue-dark/30 py-3 rounded-xl hover:bg-blue-dark hover:text-white-pure transition-colors duration-200"
                          style={{ fontSize: "11px" }}
                        >
                          ← Back
                        </button>
                      )}

                      {step < 4 ? (
                        <button
                          onClick={() => goToStep(step + 1)}
                          disabled={step === 1 ? !step1Valid : step === 2 ? !step2Valid : !step3Valid}
                          className="flex-[3] bg-blue-dark text-white-pure font-cinzel uppercase tracking-widest py-3 rounded-xl hover:bg-blue-primary transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          style={{ fontSize: "12px", letterSpacing: "0.15em" }}
                        >
                          {step === 3 ? "Review →" : "Continue →"}
                        </button>
                      ) : (
                        <button
                          onClick={handleSubmit}
                          disabled={submitting}
                          className="flex-[3] bg-blue-dark text-white-pure font-cinzel uppercase tracking-widest py-3 rounded-xl hover:bg-blue-primary transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          style={{ fontSize: "12px", letterSpacing: "0.15em" }}
                        >
                          {submitting ? "Submitting..." : "Submit Request"}
                          {!submitting && (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-center font-constantia mt-6" style={{ fontSize: "13px", color: "rgba(1,7,4,0.45)" }}>
                  Not a current policyholder?{" "}
                  <button onClick={() => navigate("/quote")} className="text-blue-dark underline hover:text-blue-primary transition-colors">
                    Request a free quote
                  </button>
                </p>
              </>
            )}
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
